import { usePublicProjectReader } from '@/lib/public-project-reader';
import { useState, useCallback, useEffect, useRef, type CSSProperties } from 'react';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import {
    X,
    Calendar,
    User,
    Clock,
    Tag,
    Edit2,
    Trash2,
    ChevronDown,
    ChevronRight,
    Loader2,
    ListTree,
    Plus,
    Flag,
    Link2,
    X as XIcon,
} from 'lucide-react';
import { taskKeys, useTask, useDeleteTask, useUpdateTask, useCreateSubtask, useAddAssignee, useRemoveAssignee, useTasks } from '@/hooks/use-tasks';
import { useRealtimeTaskDetail } from '@/hooks/use-realtime';
import { useProjectMembers } from '@/hooks/use-projects';
import { useCustomFields } from '@/hooks/use-custom-fields';
import { useTaskDependencies, useCreateTaskDependency, useDeleteTaskDependency, dependenciesWhereTaskIsSuccessor } from '@/hooks/use-task-dependencies';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { CommentsSection } from './comments-section';
import { AttachmentsSection } from './attachments-section';
import { statusConfig, priorityConfig, getStatusLabel, getPriorityLabel } from './task-table';
import { useI18n } from '@/lib/i18n';
import { apiPatch } from '@/lib/api';
import type { Task, TaskStatus, TaskPriority, CustomField } from '@/lib/schemas';

// ============================================
// HELPERS
// ============================================

function getInitials(name: string) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

// ============================================
// SUBTASK ITEM
// ============================================

interface SubtaskItemProps {
    subtask: Task;
    onSelect: (task: Task) => void;
    onStatusChange: (taskId: string, status: TaskStatus) => void;
    isUpdating: boolean;
    canEdit: boolean;
}

function SubtaskItem({ subtask, onSelect, onStatusChange, isUpdating, canEdit }: SubtaskItemProps) {
    const { t } = useI18n();
    const statusInfo = statusConfig[subtask.status];
    const StatusIcon = statusInfo.icon;

    return (
        <div
            className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-hover cursor-pointer transition-colors"
            onClick={() => onSelect(subtask)}
        >
            <Select
                value={subtask.status}
                onValueChange={(value) => onStatusChange(subtask.id, value as TaskStatus)}
                disabled={isUpdating || !canEdit}
            >
                <SelectTrigger
                    className="h-7 w-7 shrink-0 justify-center rounded-full border-0 bg-white/5 p-0 [&>svg:last-child]:hidden [&>span]:line-clamp-none"
                    onClick={(event) => event.stopPropagation()}
                    aria-label={`${t('tasks.status')}: ${getStatusLabel(subtask.status, t)}`}
                    title={`${t('tasks.status')}: ${getStatusLabel(subtask.status, t)}`}
                >
                    {isUpdating ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    ) : (
                        <StatusIcon className={`h-4 w-4 ${statusInfo.color}`} />
                    )}
                </SelectTrigger>
                <SelectContent align="start" onClick={(event) => event.stopPropagation()}>
                    {Object.entries(statusConfig).map(([key, config]) => {
                        const Icon = config.icon;
                        return (
                            <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                    <Icon className={`h-4 w-4 ${config.color}`} />
                                    <span>{getStatusLabel(key as TaskStatus, t)}</span>
                                </div>
                            </SelectItem>
                        );
                    })}
                </SelectContent>
            </Select>
            <span className="text-sm flex-1 truncate">{subtask.title}</span>
            {subtask.subtaskCount > 0 && (
                <Badge variant="outline" className="text-xs">
                    {subtask.subtaskCount} subtasks
                </Badge>
            )}
        </div>
    );
}

// ============================================
// TASK DETAIL PANEL
// ============================================

interface TaskDetailPanelProps {
    canEdit?: boolean;
    taskId: string;
    projectId: string;
    onClose: () => void;
    onEdit: (task: Task) => void;
    onSubtaskSelect?: (task: Task) => void;
    onAskAi?: (task: Task) => void;
}

const PANEL_WIDTH_STORAGE_KEY = 'project-manager:task-panel-width';
const DEFAULT_PANEL_WIDTH = 520;
const MIN_PANEL_WIDTH = 360;

function clampPanelWidth(width: number) {
    if (typeof window === 'undefined') return Math.max(MIN_PANEL_WIDTH, width);
    return Math.max(MIN_PANEL_WIDTH, Math.min(width, window.innerWidth * 0.9));
}

export function TaskDetailPanel({
    taskId,
    projectId,
    onClose,
    onEdit,
    onSubtaskSelect,
    onAskAi,
    canEdit: requestedCanEdit = true,
}: TaskDetailPanelProps) {
    const { t } = useI18n();
    const publicReader = usePublicProjectReader();
    const canEdit = requestedCanEdit && !publicReader;
    const queryClient = useQueryClient();
    const { data: task, isLoading, error } = useTask(taskId);
    const updateTask = useUpdateTask(taskId, projectId);
    const deleteTask = useDeleteTask(projectId);
    const createSubtask = useCreateSubtask(taskId, projectId);
    // t12 #5: project members + custom fields power the inline
    // assignee / custom-field editors in the meta section below.
    const { data: projectMembersData } = useProjectMembers(projectId);
    const { data: customFieldsData } = useCustomFields(projectId);
    const addAssignee = useAddAssignee(taskId, projectId);
    const removeAssignee = useRemoveAssignee(taskId, projectId);

    // t14: Gantt dependencies — the side panel exposes a "Depends on"
    // section listing other tasks in this project. We also need the
    // project's full task list so the picker can autocomplete.
    const { data: dependenciesData } = useTaskDependencies(projectId);
    const projectTasksQuery = useTasks(projectId, {includeSubtasks: true});
    const projectTasksData = projectTasksQuery.data;
    const {hasNextPage: hasMoreProjectTasks, isFetching: fetchingProjectTasks, fetchNextPage: fetchMoreProjectTasks} = projectTasksQuery;
    useEffect(() => {
        if (hasMoreProjectTasks && !fetchingProjectTasks && (projectTasksData?.tasks.length ?? 0) < 10000) void fetchMoreProjectTasks();
    }, [hasMoreProjectTasks, fetchingProjectTasks, fetchMoreProjectTasks, projectTasksData?.tasks.length]);
    const createDependency = useCreateTaskDependency(projectId);
    const deleteDependency = useDeleteTaskDependency(projectId);
    const [depPickerOpen, setDepPickerOpen] = useState(!canEdit);
    const [depSearch, setDepSearch] = useState('');

    // Real-time updates for task detail, comments, and attachments
    useRealtimeTaskDetail(publicReader ? '' : taskId);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [showSubtasks, setShowSubtasks] = useState(true);
    const [showComments, setShowComments] = useState(true);
    const [showAttachments, setShowAttachments] = useState(true);
    const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
    const [isAddingSubtask, setIsAddingSubtask] = useState(false);
    const [updatingSubtaskId, setUpdatingSubtaskId] = useState<string | null>(null);
    const [panelWidth, setPanelWidth] = useState(() => {
        if (typeof window === 'undefined') return DEFAULT_PANEL_WIDTH;
        const savedWidth = Number(window.localStorage.getItem(PANEL_WIDTH_STORAGE_KEY));
        return clampPanelWidth(Number.isFinite(savedWidth) && savedWidth > 0 ? savedWidth : DEFAULT_PANEL_WIDTH);
    });
    const [isResizing, setIsResizing] = useState(false);
    const panelWidthRef = useRef(panelWidth);
    const resizeStartRef = useRef({ pointerX: 0, width: panelWidth });

    const updatePanelWidth = useCallback((width: number) => {
        const nextWidth = clampPanelWidth(width);
        panelWidthRef.current = nextWidth;
        setPanelWidth(nextWidth);
    }, []);

    const handleResizeStart = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (window.innerWidth < 640) return;
        event.preventDefault();
        event.stopPropagation();
        resizeStartRef.current = { pointerX: event.clientX, width: panelWidthRef.current };
        setIsResizing(true);
    }, []);

    const handleResizeKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        updatePanelWidth(panelWidthRef.current + (event.key === 'ArrowLeft' ? 24 : -24));
        window.localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(panelWidthRef.current));
    }, [updatePanelWidth]);

    useEffect(() => {
        if (!isResizing) return;

        const handlePointerMove = (event: PointerEvent) => {
            const movement = resizeStartRef.current.pointerX - event.clientX;
            updatePanelWidth(resizeStartRef.current.width + movement);
        };
        const handlePointerUp = () => {
            window.localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, String(panelWidthRef.current));
            setIsResizing(false);
        };

        const previousCursor = document.body.style.cursor;
        const previousUserSelect = document.body.style.userSelect;
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp, { once: true });

        return () => {
            document.body.style.cursor = previousCursor;
            document.body.style.userSelect = previousUserSelect;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
        };
    }, [isResizing, updatePanelWidth]);

    useEffect(() => {
        const handleWindowResize = () => updatePanelWidth(panelWidthRef.current);
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [updatePanelWidth]);

    const panelStyle = {
        '--task-panel-width': `${panelWidth}px`,
    } as CSSProperties;
    const panelClassName = 'fixed inset-0 sm:inset-auto sm:right-0 sm:top-0 sm:bottom-0 w-full sm:w-[var(--task-panel-width)] sm:max-w-[90vw] bg-[#16161e] sm:border-l border-white/10 z-50 flex flex-col shadow-xl animate-in slide-in-from-right duration-300 overflow-hidden max-w-full';
    const resizeHandle = (
        <div
            role="separator"
            aria-label={t('tasks.resizePanel')}
            aria-orientation="vertical"
            aria-valuemin={MIN_PANEL_WIDTH}
            aria-valuemax={typeof window === 'undefined' ? DEFAULT_PANEL_WIDTH : Math.round(window.innerWidth * 0.9)}
            aria-valuenow={Math.round(panelWidth)}
            tabIndex={0}
            onPointerDown={handleResizeStart}
            onKeyDown={handleResizeKeyDown}
            className={`group absolute left-0 top-0 bottom-0 z-10 hidden w-3 cursor-col-resize items-center justify-center outline-none sm:flex ${isResizing ? 'bg-primary/10' : ''}`}
            style={{ touchAction: 'none' }}
        >
            <span className={`h-16 w-1 rounded-full transition-colors ${isResizing ? 'bg-primary' : 'bg-white/20 group-hover:bg-primary/70 group-focus:bg-primary/70'}`} />
        </div>
    );

    const handleStatusChange = useCallback(async (status: TaskStatus) => {
        if (!canEdit) return;
        try {
            await updateTask.mutateAsync({ status });
        } catch (error) {
            console.error('Error updating status:', error);
        }
    }, [canEdit, updateTask]);

    const handlePriorityChange = useCallback(async (priority: TaskPriority) => {
        if (!canEdit) return;
        try {
            await updateTask.mutateAsync({ priority });
        } catch (error) {
            console.error('Error updating priority:', error);
        }
    }, [canEdit, updateTask]);

    // t12 #5: inline edits for due date, assignees, and one custom
    // field. Each handler PATCHes /api/tasks/:id (or the assignees
    // subresource) and lets useUpdateTask's onSuccess refetch the
    // detail — no manual cache patching needed.
    //
    // R2: <Input type="date"> hands us a date-only "YYYY-MM-DD" string.
    // The backend's updateTaskSchema validates dueDate / startDate /
    // endDate as z.string().datetime() (ISO 8601) so we have to lift
    // the value into a full ISO datetime before sending — without this
    // the PATCH is rejected with 400 and the user sees nothing change.
    const toIsoDate = (newDate: string | null): string | null => {
        if (!newDate) return null;
        const parsed = new Date(newDate);
        if (isNaN(parsed.getTime())) return null;
        return parsed.toISOString();
    };

    const handleDueDateChange = useCallback(async (newDate: string | null) => {
        if (!canEdit) return;
        try {
            await updateTask.mutateAsync({ dueDate: toIsoDate(newDate) });
        } catch (error) {
            console.error('Error updating due date:', error);
        }
    }, [canEdit, updateTask]);

    // R2: editable startDate / endDate. The backend's status hook
    // (task-status-hooks.service.ts) overwrites these on IN_PROGRESS /
    // DONE transitions, but explicit user edits via the panel stick as
    // long as the user avoids the triggering status changes.
    const handleStartDateChange = useCallback(async (newDate: string | null) => {
        if (!canEdit) return;
        try {
            await updateTask.mutateAsync({ startDate: toIsoDate(newDate) });
        } catch (error) {
            console.error('Error updating start date:', error);
        }
    }, [canEdit, updateTask]);

    const handleEndDateChange = useCallback(async (newDate: string | null) => {
        if (!canEdit) return;
        try {
            await updateTask.mutateAsync({ endDate: toIsoDate(newDate) });
        } catch (error) {
            console.error('Error updating end date:', error);
        }
    }, [canEdit, updateTask]);

    const handleAssigneeToggle = useCallback(async (userId: string) => {
        if (!canEdit) return;
        const currentIds = task?.assignees?.map((a) => a.userId) ?? [];
        const isAssigned = currentIds.includes(userId);
        try {
            if (isAssigned) {
                await removeAssignee.mutateAsync(userId);
            } else {
                await addAssignee.mutateAsync(userId);
            }
        } catch (error) {
            console.error('Error updating assignee:', error);
        }
    }, [canEdit, task?.assignees, addAssignee, removeAssignee]);

    const handleCustomFieldChange = useCallback(async (fieldId: string, value: unknown) => {
        if (!canEdit) return;
        const nextValues = {
            ...(task?.customFieldValues || {}),
            [fieldId]: value,
        };
        try {
            await updateTask.mutateAsync({ customFieldValues: nextValues });
        } catch (error) {
            console.error('Error updating custom field:', error);
        }
    }, [canEdit, task?.customFieldValues, updateTask]);

    const handleSubtaskStatusChange = useCallback(async (subtaskId: string, status: TaskStatus) => {
        if (!canEdit) return;
        setUpdatingSubtaskId(subtaskId);
        try {
            await apiPatch(`/tasks/${subtaskId}`, { status });
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) }),
                queryClient.invalidateQueries({ queryKey: taskKeys.detail(subtaskId) }),
                queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] }),
            ]);
        } catch (error) {
            console.error('Error updating subtask status:', error);
        } finally {
            setUpdatingSubtaskId(null);
        }
    }, [canEdit, projectId, queryClient, taskId]);

    const handleConfirmDelete = useCallback(async () => {
        if (!canEdit) return;
        try {
            await deleteTask.mutateAsync(taskId);
            setDeleteDialogOpen(false);
            onClose();
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    }, [canEdit, taskId, deleteTask, onClose]);

    const handleAddSubtask = useCallback(async () => {
        if (!canEdit) return;
        if (!newSubtaskTitle.trim()) return;

        try {
            await createSubtask.mutateAsync({
                title: newSubtaskTitle.trim(),
                status: 'TODO',
                priority: 'NONE',
                assigneeIds: [],
                customFieldValues: {},
            });
            setNewSubtaskTitle('');
            setIsAddingSubtask(false);
        } catch (error) {
            console.error('Error creating subtask:', error);
        }
    }, [canEdit, newSubtaskTitle, createSubtask]);

    const handleSubtaskKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSubtask();
        } else if (e.key === 'Escape') {
            setNewSubtaskTitle('');
            setIsAddingSubtask(false);
        }
    }, [handleAddSubtask]);

    if (isLoading) {
        return (
            <>
                <div
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={onClose}
                />
                <div role="dialog" aria-modal="true" aria-label={t('tasks.taskDetails')} data-testid="task-detail-panel" className={panelClassName} style={panelStyle}>
                    {resizeHandle}
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                </div>
            </>
        );
    }

    if (error || !task) {
        return (
            <>
                <div
                    className="fixed inset-0 bg-black/50 z-40"
                    onClick={onClose}
                />
                <div role="dialog" aria-modal="true" aria-label={t('tasks.taskDetails')} data-testid="task-detail-panel" className={panelClassName} style={panelStyle}>
                    {resizeHandle}
                    <div className="flex flex-col items-center justify-center h-full gap-4 p-4">
                        <p className="text-white/50 text-center">{t('error.somethingWentWrong')}</p>
                        <Button variant="outline" onClick={onClose}>{t('common.close')}</Button>
                    </div>
                </div>
            </>
        );
    }

    const statusInfo = statusConfig[task.status];
    const StatusIcon = statusInfo.icon;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-40 touch-none"
                onClick={onClose}
            />

            {/* Slide-out Panel - Full screen on mobile, sidebar on desktop */}
            <div role="dialog" aria-modal="true" aria-label={t('tasks.taskDetails')} data-testid="task-detail-panel" className={panelClassName} style={panelStyle}>
                {resizeHandle}
                {/* Header */}
                <div className="flex items-center justify-between px-3 sm:px-4 py-3 border-b border-white/10 shrink-0 min-w-0 gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                        <Select disabled={!canEdit}
                            value={task.status}
                            onValueChange={(value) => handleStatusChange(value as TaskStatus)}
                        >
                            <SelectTrigger
                                className="h-8 w-8 shrink-0 justify-center rounded-full border-white/10 bg-white/5 p-0 [&>svg:last-child]:hidden [&>span]:line-clamp-none"
                                title={`${t('tasks.status')}: ${getStatusLabel(task.status, t)}`}
                                aria-label={`${t('tasks.status')}: ${getStatusLabel(task.status, t)}`}
                            >
                                <SelectValue>
                                    <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent align="start">
                                {Object.entries(statusConfig).map(([key, config]) => {
                                    const Icon = config.icon;
                                    return (
                                        <SelectItem key={key} value={key}>
                                            <div className="flex items-center gap-2">
                                                <Icon className={`h-4 w-4 ${config.color}`} />
                                                <span>{getStatusLabel(key as TaskStatus, t)}</span>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        <span className="text-sm font-medium text-white/70 truncate">
                            {t('tasks.taskDetails')}
                        </span>
                    </div>
                    <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                        {onAskAi && <Button variant="ghost" size="sm" onClick={() => onAskAi(task)}>AI</Button>}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 sm:h-9 sm:w-9"
                            disabled={!canEdit}
                            onClick={() => onEdit(task)}
                            title={t('common.edit')}
                        >
                            <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 sm:h-9 sm:w-9 text-destructive hover:text-destructive"
                            disabled={!canEdit}
                            onClick={() => setDeleteDialogOpen(true)}
                            title={t('common.delete')}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 sm:h-9 sm:w-9" onClick={onClose}>
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 max-w-full">
                    <div className="p-3 sm:p-4 space-y-4 sm:space-y-6 min-w-0">
                        {/* Title */}
                        <div className="min-w-0">
                            <h2 className="text-base sm:text-lg font-semibold text-white break-words">{task.title}</h2>
                            {task.description && (
                                <p className="mt-2 text-sm text-white/70 whitespace-pre-wrap break-words">
                                    {task.description}
                                </p>
                            )}
                        </div>

                        {/* Quick actions */}
                        <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4 min-w-0">
                            {/* Status */}
                            <div className="space-y-1.5 min-w-0">
                                <label className="text-xs text-white/50 font-medium">{t('tasks.status')}</label>
                                <Select disabled={!canEdit}
                                    value={task.status}
                                    onValueChange={(value) => handleStatusChange(value as TaskStatus)}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(statusConfig).map(([key, config]) => {
                                            const Icon = config.icon;
                                            return (
                                                <SelectItem key={key} value={key}>
                                                    <div className="flex items-center gap-2">
                                                        <Icon className={`h-4 w-4 ${config.color}`} />
                                                        <span>{getStatusLabel(key as TaskStatus, t)}</span>
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Priority */}
                            <div className="space-y-1.5">
                                <label className="text-xs text-foreground-muted font-medium">{t('tasks.priority')}</label>
                                <Select disabled={!canEdit}
                                    value={task.priority}
                                    onValueChange={(value) => handlePriorityChange(value as TaskPriority)}
                                >
                                    <SelectTrigger className="h-9">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(priorityConfig).map(([key, config]) => {
                                            const Icon = config.icon;
                                            return (
                                                <SelectItem key={key} value={key}>
                                                    <div className="flex items-center gap-2">
                                                        <Icon className={`h-4 w-4 ${config.color}`} />
                                                        <span>{getPriorityLabel(key as TaskPriority, t)}</span>
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Meta info — t12 #5: due date, assignees and one
                            custom field are inline-editable. */}
                        <div className="space-y-3">
                            {/* Due date */}
                            <div className="flex items-center gap-3">
                                <Calendar className="h-4 w-4 text-foreground-muted shrink-0" />
                                <span className="text-xs text-foreground-muted font-medium w-20 shrink-0">
                                    {t('tasks.dueDate')}
                                </span>
                                <Input
                                    type="date"
                                    disabled={!canEdit}
                                    className="h-8 flex-1"
                                    value={task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : ''}
                                    onChange={(e) => handleDueDateChange(e.target.value || null)}
                                />
                            </div>

                            {/* R2: Start / End date are user-editable (the
                                backend's task-status-hooks service still
                                overwrites them on IN_PROGRESS / DONE
                                transitions, but explicit panel edits stick
                                otherwise). They used to be read-only labels
                                in R1. */}
                            <div className="flex items-center gap-3">
                                <Flag className="h-4 w-4 text-foreground-muted shrink-0" />
                                <span className="text-xs text-foreground-muted font-medium w-20 shrink-0">
                                    {t('tasks.startDate')}
                                </span>
                                <Input
                                    type="date"
                                    disabled={!canEdit}
                                    className="h-8 flex-1"
                                    value={task.startDate ? new Date(task.startDate).toISOString().slice(0, 10) : ''}
                                    onChange={(e) => handleStartDateChange(e.target.value || null)}
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <Flag className="h-4 w-4 text-foreground-muted shrink-0" />
                                <span className="text-xs text-foreground-muted font-medium w-20 shrink-0">
                                    {t('tasks.endDate')}
                                </span>
                                <Input
                                    type="date"
                                    disabled={!canEdit}
                                    className="h-8 flex-1"
                                    value={task.endDate ? new Date(task.endDate).toISOString().slice(0, 10) : ''}
                                    onChange={(e) => handleEndDateChange(e.target.value || null)}
                                />
                            </div>

                            {/* Assignees — multi-select checkbox list. The
                                previous version only showed existing
                                assignees with no way to add/remove. */}
                            <div className="flex items-start gap-3">
                                <User className="h-4 w-4 text-foreground-muted shrink-0 mt-2" />
                                <span className="text-xs text-foreground-muted font-medium w-20 shrink-0 mt-2">
                                    {t('tasks.assignees')}
                                </span>
                                <div className="flex-1 space-y-1 max-h-32 overflow-y-auto rounded-md border border-white/10 p-2">
                                    {projectMembersData?.length ? (
                                        projectMembersData.map((m) => {
                                            const checked = task.assignees?.some((a) => a.userId === m.userId) ?? false;
                                            return (
                                                <label
                                                    key={m.userId}
                                                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-surface-hover cursor-pointer text-sm"
                                                >
                                                    <Checkbox
                                                        checked={checked}
                                                        disabled={!canEdit}
                                                        onCheckedChange={() => handleAssigneeToggle(m.userId)}
                                                    />
                                                    <span className="truncate">{m.user.name}</span>
                                                </label>
                                            );
                                        })
                                    ) : (
                                        <p className="text-xs text-foreground-muted px-2 py-1">
                                            {t('common.noData')}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* t35 (R8): ALL custom fields rendered, not
                                just the first. The previous code used
                                `(customFieldsData || []).find(...)` which
                                hid every other field. Now we iterate over
                                the full list, sorted by `order`, so the
                                user can add a value to any field
                                (even ones with no value yet). Empty fields
                                get an explicit "Add" placeholder/button. */}
                            {(customFieldsData || []).length > 0 && (
                                <div className="space-y-2 pt-2">
                                    <div className="flex items-center gap-2 text-foreground-secondary">
                                        <Tag className="h-4 w-4" />
                                        <span className="font-medium text-sm">
                                            {t('tasks.customFieldsSection')}
                                        </span>
                                        <span className="text-xs text-foreground-muted">
                                            ({customFieldsData?.length || 0})
                                        </span>
                                    </div>
                                    <div className="pl-2 space-y-2">
                                        {[...(customFieldsData || [])]
                                            .sort((a, b) => a.order - b.order)
                                            .map((cf) => {
                                                const currentValue = task.customFieldValues?.[cf.id];
                                                return (
                                                    <CustomFieldPanelRow
                                                        canEdit={canEdit}
                                                        key={cf.id}
                                                        field={cf}
                                                        value={currentValue}
                                                        onChange={(next) => handleCustomFieldChange(cf.id, next)}
                                                        projectMembersData={projectMembersData}
                                                    />
                                                );
                                            })}
                                    </div>
                                </div>
                            )}

                            {/* Created at — read-only. */}
                            <div className="flex items-center gap-3">
                                <Clock className="h-4 w-4 text-foreground-muted shrink-0" />
                                <span className="text-xs text-foreground-muted font-medium w-20 shrink-0">
                                    {t('tasks.createdAt')}
                                </span>
                                <span className="text-sm text-foreground-secondary flex-1">
                                    {format(new Date(task.createdAt), 'MMM d, yyyy')}
                                </span>
                            </div>
                        </div>

                        {/* Subtasks */}
                        <div className="space-y-2">
                            <button
                                className="flex items-center gap-2 text-foreground-secondary hover:text-foreground transition-colors w-full"
                                onClick={() => setShowSubtasks(!showSubtasks)}
                            >
                                {showSubtasks ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                <ListTree className="h-4 w-4" />
                                <span className="font-medium text-sm">
                                    {t('tasks.subtasks')} ({task.subtasks?.length || 0})
                                </span>
                            </button>
                            {showSubtasks && (
                                <div className="pl-2 space-y-1">
                                    {/* Existing subtasks */}
                                    {task.subtasks && task.subtasks.map((subtask) => (
                                        <SubtaskItem
                                            key={subtask.id}
                                            subtask={subtask}
                                            onSelect={(t) => onSubtaskSelect?.(t)}
                                            onStatusChange={handleSubtaskStatusChange}
                                            isUpdating={updatingSubtaskId === subtask.id}
                                            canEdit={canEdit}
                                        />
                                    ))}

                                    {/* Add subtask input */}
                                    {canEdit && (isAddingSubtask ? (
                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-3 py-2">
                                            <Input
                                                placeholder={t('tasks.taskName') + '...'}
                                                value={newSubtaskTitle}
                                                onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                                onKeyDown={handleSubtaskKeyDown}
                                                autoFocus
                                                className="h-8 text-sm flex-1"
                                            />
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={handleAddSubtask}
                                                    disabled={!newSubtaskTitle.trim() || createSubtask.isPending}
                                                    className="flex-1 sm:flex-none"
                                                >
                                                    {createSubtask.isPending ? (
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                    ) : (
                                                        t('common.add')
                                                    )}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => {
                                                        setNewSubtaskTitle('');
                                                        setIsAddingSubtask(false);
                                                    }}
                                                    className="flex-1 sm:flex-none"
                                                >
                                                    {t('common.cancel')}
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button
                                            className="flex items-center gap-2 px-3 py-2 text-sm text-foreground-muted hover:text-foreground hover:bg-surface-hover rounded-lg transition-colors w-full"
                                            onClick={() => setIsAddingSubtask(true)}
                                        >
                                            <Plus className="h-4 w-4" />
                                            <span>{t('tasks.addSubtask')}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border" />

                        {/* Depends on (t14) — multi-select dependency
                            picker. Renders the list of predecessors with
                            an inline delete button + a combobox to add
                            new ones. Filters out the current task + any
                            task already added as a dependency. */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <button
                                    className="flex items-center gap-2 text-foreground-secondary hover:text-foreground transition-colors"
                                    onClick={() => setDepPickerOpen(!depPickerOpen)}
                                >
                                    {depPickerOpen ? (
                                        <ChevronDown className="h-4 w-4" />
                                    ) : (
                                        <ChevronRight className="h-4 w-4" />
                                    )}
                                    <Link2 className="h-4 w-4" />
                                    <span className="font-medium text-sm">
                                        {t('tasks.dependsOn')}
                                        {' '}
                                        ({(() => {
                                            const deps = dependenciesData?.dependencies || [];
                                            return dependenciesWhereTaskIsSuccessor(deps, taskId).length;
                                        })()})
                                    </span>
                                </button>
                            </div>
                            {depPickerOpen && (() => {
                                const deps = dependenciesData?.dependencies || [];
                                const myIncomingDeps = dependenciesWhereTaskIsSuccessor(deps, taskId);
                                // Build a lookup of project tasks so we can
                                // show the predecessor title + status.
                                const projectTasks = projectTasksData?.tasks || [];
                                const taskById = new Map(projectTasks.map((p) => [p.id, p]));

                                // Filter the picker to tasks that are NOT
                                // the current task and NOT already a
                                // predecessor. Also case-insensitive search
                                // on title.
                                const q = depSearch.trim().toLowerCase();
                                const alreadyPredecessorIds = new Set(
                                    myIncomingDeps.map((d) => d.predecessorId)
                                );
                                const candidates = projectTasks.filter(
                                    (t) =>
                                        t.id !== taskId &&
                                        !alreadyPredecessorIds.has(t.id) &&
                                        (q === '' || t.title.toLowerCase().includes(q))
                                );

                                return (
                                    <div className="pl-2 space-y-2">
                                        {/* Existing deps */}
                                        {myIncomingDeps.length === 0 ? (
                                            <p className="text-xs text-foreground-muted px-2 py-1">
                                                {t('tasks.dependsOnEmpty')}
                                            </p>
                                        ) : (
                                            myIncomingDeps.map((d) => {
                                                const predecessor = taskById.get(d.predecessorId);
                                                return (
                                                    <div
                                                        key={d.id}
                                                        className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border border-white/10 hover:bg-surface-hover"
                                                    >
                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                            <Badge
                                                                variant="secondary"
                                                                className="text-xs shrink-0"
                                                                title={`Dependency type: ${d.type}`}
                                                            >
                                                                {d.type}
                                                            </Badge>
                                                            <span className="text-sm truncate">
                                                                {predecessor?.title || d.predecessorId}
                                                            </span>
                                                        </div>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
                                                            onClick={() => deleteDependency.mutate(d.id)}
                                                            disabled={deleteDependency.isPending || !canEdit}
                                                            title={t('common.delete')}
                                                        >
                                                            <XIcon className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                );
                                            })
                                        )}

                                        {/* Picker */}
                                        {canEdit && <div className="space-y-1.5 px-1 pt-1">
                                            <Input
                                                placeholder={t('tasks.dependsOnSearchPlaceholder')}
                                                value={depSearch}
                                                onChange={(e) => setDepSearch(e.target.value)}
                                                className="h-8 text-sm"
                                            />
                                            <div className="max-h-40 overflow-y-auto rounded-md border border-white/10">
                                                {candidates.length === 0 ? (
                                                    <p className="text-xs text-foreground-muted px-2 py-2">
                                                        {q === ''
                                                            ? t('tasks.dependsOnEmpty')
                                                            : t('tasks.dependsOnNoMatches')}
                                                    </p>
                                                ) : (
                                                    candidates.slice(0, 50).map((candidate) => (
                                                        <button
                                                            key={candidate.id}
                                                            type="button"
                                                            className="w-full text-left px-2 py-1.5 text-sm hover:bg-surface-hover flex items-center gap-2"
                                                            onClick={() => {
                                                                createDependency.mutate({
                                                                    predecessorId: candidate.id,
                                                                    successorId: taskId,
                                                                    type: 'FS',
                                                                });
                                                                setDepSearch('');
                                                            }}
                                                            disabled={createDependency.isPending}
                                                        >
                                                            <Plus className="h-3.5 w-3.5 text-foreground-muted shrink-0" />
                                                            <span className="truncate flex-1">{candidate.title}</span>
                                                            <Badge variant="secondary" className="text-[10px] shrink-0">
                                                                {candidate.status}
                                                            </Badge>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        </div>}
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border" />

                        {/* Attachments */}
                        <div className="space-y-2">
                            <button
                                className="flex items-center gap-2 text-foreground-secondary hover:text-foreground transition-colors w-full"
                                onClick={() => setShowAttachments(!showAttachments)}
                            >
                                {showAttachments ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-medium text-sm">{t('tasks.attachments')}</span>
                            </button>
                            {showAttachments && <AttachmentsSection taskId={taskId} canEdit={canEdit} />}
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border" />

                        {/* Comments */}
                        <div className="space-y-2">
                            <button
                                className="flex items-center gap-2 text-foreground-secondary hover:text-foreground transition-colors w-full"
                                onClick={() => setShowComments(!showComments)}
                            >
                                {showComments ? (
                                    <ChevronDown className="h-4 w-4" />
                                ) : (
                                    <ChevronRight className="h-4 w-4" />
                                )}
                                <span className="font-medium text-sm">{t('tasks.comments')}</span>
                            </button>
                            {showComments && <CommentsSection taskId={taskId} projectId={projectId} canEdit={canEdit} />}
                        </div>
                    </div>
                </div>

                {/* Delete confirmation dialog */}
                <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{t('tasks.deleteTask')}</DialogTitle>
                            <DialogDescription>
                                {t('confirm.deleteTask')}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setDeleteDialogOpen(false)}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmDelete}
                                disabled={deleteTask.isPending}
                            >
                                {deleteTask.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    t('common.delete')
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
}

// ============================================
// CUSTOM FIELD INLINE EDITOR
// ============================================
//
// t12 #5: a tiny inline editor that renders the right input shape
// for a single CustomField and reports changes via onChange. Lives at
// the bottom of the file so it doesn't expand the main component.

// ============================================
// t35 (R8): Side-panel custom-field row
// ============================================
//
// Renders a SINGLE custom field as a row inside the panel's custom-
// fields section. Each row has:
//   - The field's name (left column, fixed width)
//   - The value/editor (right column, takes remaining width)
//   - An empty-state hint + "Add" affordance when the field has no
//     value yet, so the user can always set one.
//
// The editor shape mirrors the table cell editors: Popover for
// MULTI_SELECT, DropdownMenu for SELECT / SPRINT / PERSON, inline
// <Input> for TEXT/NUMBER/URL/EMAIL/DATE, Checkbox for CHECKBOX.
// PERSON (member picker) is new here — the previous single-field
// editor didn't handle it.

// ============================================
// t37 (R9): Side-panel custom-field row
// ============================================
//
// Per-row state machine: each row is either in "display" mode or
// "edit" mode.
//   - Display mode (default):
//       - Empty field → dashed-border "+ Add" BUTTON (click → edit)
//       - Has value  → clickable pill/badge (click → edit)
//   - Edit mode:
//       - Renders the inline editor (input/dropdown/popover/etc.)
//       - Text inputs: Enter commits, Escape cancels, blur commits
//       - Popovers/dropdowns: selection commits immediately,
//         outside-click cancels (auto via Radix onOpenChange)
//
// The state is LOCAL (per row) and resets whenever `value` changes
// from the parent — so external PATCHes (e.g., from realtime WS)
// don't strand the row in edit mode.

function CustomFieldPanelRow({
    canEdit = true,
    field,
    value,
    onChange,
    projectMembersData,
}: {
    canEdit?: boolean;
    field: CustomField;
    value: unknown;
    onChange: (next: unknown) => void;
    projectMembersData?: Array<{
        userId: string;
        user: { id: string; name: string; email: string; avatarUrl: string | null };
    }>;
}) {
    const { t } = useI18n();
    const [isEditing, setIsEditing] = useState(false);

    // Per-type empty-state check.
    const hasValue = (() => {
        if (value === null || value === undefined) return false;
        if (typeof value === 'string') return value.length > 0;
        if (typeof value === 'number') return true;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'boolean') return true;
        return false;
    })();

    // commit(): write value via onChange, leave edit mode.
    const commit = (next: unknown) => {
        onChange(next);
        setIsEditing(false);
    };
    // cancel(): leave edit mode WITHOUT writing.
    const cancel = () => setIsEditing(false);

    // Helper: render the display-mode affordance (the "+ Add" button
    // when empty, or the clickable value pill when full). Clicking
    // either enters edit mode.
    const renderDisplay = (valueNode: React.ReactNode) => (
        <div className="flex-1">
            {hasValue ? (
                <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7 inline-flex items-center"
                >
                    {valueNode}
                </button>
            ) : (
                <button
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setIsEditing(true)}
                    className="text-xs text-foreground-muted border border-dashed border-white/15 hover:border-white/30 hover:text-foreground rounded px-2 py-1 inline-flex items-center gap-1 transition-colors"
                >
                    <Plus className="h-3 w-3" />
                    {t('tasks.customFieldAdd')}
                </button>
            )}
        </div>
    );

    // SELECT — DropdownMenu (matches table mode).
    if (field.type === 'SELECT') {
        const selectedOption = field.options.find((opt) => opt.id === value);
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        selectedOption ? (
                            <Badge
                                variant="secondary"
                                className="text-xs"
                                style={selectedOption.color ? {
                                    backgroundColor: `${selectedOption.color}20`,
                                    borderColor: selectedOption.color,
                                    color: selectedOption.color,
                                } : undefined}
                            >
                                {selectedOption.label}
                            </Badge>
                        ) : (
                            <span className="text-foreground-muted text-sm">—</span>
                        )
                    )}
                </div>
            );
        }
        // Edit mode: open the dropdown immediately (defaultOpen) so the
        // user can pick without a second click. Selecting an option
        // commits + closes. Outside-click cancels.
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <div className="flex-1">
                    <DropdownMenu defaultOpen onOpenChange={(open) => { if (!open) cancel(); }}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="flex items-center gap-1 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7"
                            >
                                {selectedOption ? (
                                    <Badge
                                        variant="secondary"
                                        className="text-xs"
                                        style={selectedOption.color ? {
                                            backgroundColor: `${selectedOption.color}20`,
                                            borderColor: selectedOption.color,
                                            color: selectedOption.color,
                                        } : undefined}
                                    >
                                        {selectedOption.label}
                                    </Badge>
                                ) : (
                                    <span className="text-foreground-muted text-sm">—</span>
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => commit(null)}>
                                <span className="text-foreground-muted">{t('tasks.customFieldNone')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {field.options.map((opt) => (
                                <DropdownMenuItem
                                    key={opt.id}
                                    onClick={() => commit(opt.id)}
                                >
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                        style={{ backgroundColor: opt.color || '#6366f1' }}
                                    >
                                        {opt.label}
                                    </span>
                                    {value === opt.id && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        );
    }

    // MULTI_SELECT — Popover with checkboxes (matches table mode).
    if (field.type === 'MULTI_SELECT') {
        const selectedIds = Array.isArray(value)
            ? value.filter((v): v is string => typeof v === 'string')
            : [];
        const selectedOptions = field.options.filter((opt) => selectedIds.includes(opt.id));
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        selectedOptions.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                                {selectedOptions.map((opt) => (
                                    <Badge
                                        key={opt.id}
                                        variant="secondary"
                                        className="text-xs"
                                        style={opt.color ? {
                                            backgroundColor: `${opt.color}20`,
                                            borderColor: opt.color,
                                            color: opt.color,
                                        } : undefined}
                                    >
                                        {opt.label}
                                    </Badge>
                                ))}
                            </div>
                        ) : (
                            <span className="text-foreground-muted text-sm">—</span>
                        )
                    )}
                </div>
            );
        }
        // Edit mode: open the popover immediately. Selecting an
        // option commits. Click-outside commits (Radix closes on
        // outside click by default; the popover doesn't keep state).
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <div className="flex-1">
                    <Popover defaultOpen onOpenChange={(open) => { if (!open) cancel(); }}>
                        <PopoverTrigger asChild>
                            <button
                                type="button"
                                className="flex flex-wrap gap-1 items-center cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7"
                            >
                                {selectedOptions.length > 0 ? (
                                    selectedOptions.map((opt) => (
                                        <Badge
                                            key={opt.id}
                                            variant="secondary"
                                            className="text-xs"
                                            style={opt.color ? {
                                                backgroundColor: `${opt.color}20`,
                                                borderColor: opt.color,
                                                color: opt.color,
                                            } : undefined}
                                        >
                                            {opt.label}
                                        </Badge>
                                    ))
                                ) : (
                                    <span className="text-foreground-muted text-sm">—</span>
                                )}
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-2" align="start">
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                                {field.options.map((opt) => {
                                    const checked = selectedIds.includes(opt.id);
                                    return (
                                        <div
                                            key={opt.id}
                                            className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-surface-hover cursor-pointer"
                                            onClick={() => {
                                                const next = checked
                                                    ? selectedIds.filter((id) => id !== opt.id)
                                                    : [...selectedIds, opt.id];
                                                commit(next);
                                            }}
                                        >
                                            <Checkbox checked={checked} />
                                            <span
                                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                                style={{ backgroundColor: opt.color || '#6366f1' }}
                                            >
                                                {opt.label}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </PopoverContent>
                    </Popover>
                </div>
            </div>
        );
    }

    // SPRINT — DropdownMenu with colour dot + date range (matches table).
    if (field.type === 'SPRINT') {
        const sprints = parseSprintOptionsForField(field.options);
        const currentId = typeof value === 'string' ? value : null;
        const activeSprint = sprints.find((s) => s.id === currentId);
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        activeSprint ? (
                            <span className="inline-flex items-center gap-2">
                                <span
                                    className="inline-block w-2 h-2 rounded-full shrink-0"
                                    style={{ backgroundColor: activeSprint.color || '#6366f1' }}
                                />
                                <span className="text-sm">{activeSprint.name}</span>
                            </span>
                        ) : (
                            <span className="text-foreground-muted text-sm">—</span>
                        )
                    )}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <div className="flex-1">
                    <DropdownMenu defaultOpen onOpenChange={(open) => { if (!open) cancel(); }}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7"
                            >
                                {activeSprint ? (
                                    <>
                                        <span
                                            className="inline-block w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: activeSprint.color || '#6366f1' }}
                                        />
                                        <span className="text-sm">{activeSprint.name}</span>
                                    </>
                                ) : (
                                    <span className="text-foreground-muted text-sm">—</span>
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => commit(null)}>
                                <span className="text-foreground-muted">{t('tasks.customFieldNone')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {sprints.map((s) => (
                                <DropdownMenuItem
                                    key={s.id}
                                    onClick={() => commit(s.id)}
                                >
                                    <span
                                        className="inline-block w-2 h-2 rounded-full shrink-0"
                                        style={{ backgroundColor: s.color || '#6366f1' }}
                                    />
                                    <span>{s.name}</span>
                                    {s.startDate || s.endDate ? (
                                        <span className="text-foreground-muted text-xs ml-2">
                                            ({s.startDate || '?'} → {s.endDate || '?'})
                                        </span>
                                    ) : null}
                                    {currentId === s.id && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        );
    }

    // PERSON — DropdownMenu with project-member list (matches table).
    if (field.type === 'PERSON') {
        const selectedUserId = typeof value === 'string' ? value : null;
        const selectedMember = (projectMembersData || []).find(
            (m) => m.userId === selectedUserId
        );
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        selectedMember ? (
                            <span className="inline-flex items-center gap-2">
                                <Avatar className="h-5 w-5">
                                    <AvatarImage src={selectedMember.user.avatarUrl || undefined} />
                                    <AvatarFallback className="text-[10px]">
                                        {getInitials(selectedMember.user.name)}
                                    </AvatarFallback>
                                </Avatar>
                                <span className="text-sm">{selectedMember.user.name}</span>
                            </span>
                        ) : (
                            <span className="text-foreground-muted text-sm">—</span>
                        )
                    )}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <div className="flex-1">
                    <DropdownMenu defaultOpen onOpenChange={(open) => { if (!open) cancel(); }}>
                        <DropdownMenuTrigger asChild>
                            <button
                                type="button"
                                className="flex items-center gap-2 cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7"
                            >
                                {selectedMember ? (
                                    <>
                                        <Avatar className="h-5 w-5">
                                            <AvatarImage src={selectedMember.user.avatarUrl || undefined} />
                                            <AvatarFallback className="text-[10px]">
                                                {getInitials(selectedMember.user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm">{selectedMember.user.name}</span>
                                    </>
                                ) : (
                                    <span className="text-foreground-muted text-sm">—</span>
                                )}
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuItem onClick={() => commit(null)}>
                                <span className="text-foreground-muted">{t('tasks.customFieldNone')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {(projectMembersData || []).map((member) => (
                                <DropdownMenuItem
                                    key={member.userId}
                                    onClick={() => commit(member.userId)}
                                >
                                    <Avatar className="h-5 w-5">
                                        <AvatarImage src={member.user.avatarUrl || undefined} />
                                        <AvatarFallback className="text-[10px]">
                                            {getInitials(member.user.name)}
                                        </AvatarFallback>
                                    </Avatar>
                                    <span>{member.user.name}</span>
                                    {selectedUserId === member.userId && <span className="ml-auto">✓</span>}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>
        );
    }

    // DATE — date input. Always-visible input with commit-on-blur.
    if (field.type === 'DATE') {
        const dateValue = typeof value === 'string' ? value.slice(0, 10) : '';
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        hasValue ? (
                            <span className="text-sm">{dateValue}</span>
                        ) : (
                            <span className="text-foreground-muted text-sm">—</span>
                        )
                    )}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <Input
                    type="date"
                    autoFocus
                    className="h-8 flex-1"
                    defaultValue={dateValue}
                    onBlur={(e) => commit(e.target.value || null)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            cancel();
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            commit(e.currentTarget.value || null);
                        }
                    }}
                />
            </div>
        );
    }

    // CHECKBOX — toggle button. Click the empty-state button to
    // enable (commits true). When already enabled, click the value
    // pill to disable (commits false). The checkbox semantics are
    // simple enough that we skip the two-mode edit flow and commit
    // on every click.
    if (field.type === 'CHECKBOX') {
        const checked = Boolean(value);
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                {hasValue ? (
                    <div className="flex-1">
                        <button
                            type="button"
                            onClick={() => onChange(!checked)}
                            className="cursor-pointer hover:bg-surface-hover rounded px-1 py-0.5 min-h-7 inline-flex items-center gap-2"
                        >
                            <Checkbox checked={checked} />
                            <span className="text-xs text-foreground-muted">
                                {checked ? t('common.yes') : t('common.no')}
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className="flex-1">
                        <button
                            type="button"
                            onClick={() => onChange(true)}
                            className="text-xs text-foreground-muted border border-dashed border-white/15 hover:border-white/30 hover:text-foreground rounded px-2 py-1 inline-flex items-center gap-1 transition-colors"
                        >
                            <Plus className="h-3 w-3" />
                            {t('tasks.customFieldAdd')}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // NUMBER — number input.
    if (field.type === 'NUMBER') {
        if (!isEditing) {
            return (
                <div className="flex items-center gap-3 min-h-8">
                    <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                        {field.name}
                    </span>
                    {renderDisplay(
                        <span className="text-sm">
                            {typeof value === 'number' ? value : '—'}
                        </span>
                    )}
                </div>
            );
        }
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                <Input
                    type="number"
                    autoFocus
                    className="h-8 flex-1"
                    defaultValue={typeof value === 'number' ? value : ''}
                    onBlur={(e) => {
                        const v = e.target.value;
                        commit(v === '' ? null : Number(v));
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            cancel();
                        } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const v = e.currentTarget.value;
                            commit(v === '' ? null : Number(v));
                        }
                    }}
                />
            </div>
        );
    }

    // TEXT / URL / EMAIL — plain text input.
    if (!isEditing) {
        return (
            <div className="flex items-center gap-3 min-h-8">
                <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                    {field.name}
                </span>
                {renderDisplay(
                    <span className="text-sm truncate">
                        {typeof value === 'string' && value.length > 0 ? value : '—'}
                    </span>
                )}
            </div>
        );
    }
    const inputType = field.type === 'URL' ? 'url' : field.type === 'EMAIL' ? 'email' : 'text';
    return (
        <div className="flex items-center gap-3 min-h-8">
            <span className="text-xs text-foreground-muted font-medium w-24 shrink-0 truncate">
                {field.name}
            </span>
            <Input
                type={inputType}
                autoFocus
                className="h-8 flex-1"
                defaultValue={typeof value === 'string' ? value : ''}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        cancel();
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        commit(e.currentTarget.value);
                    }
                }}
            />
        </div>
    );
}

// Local sprint parser — duplicated here to avoid pulling
// custom-field-manager into the panel bundle. The shape is identical
// to `parseSprintOptions` in custom-field-manager.tsx; keep both in
// sync if you extend either.
function parseSprintOptionsForField(raw: unknown): Array<{
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    color?: string;
}> {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
    const obj = raw as Record<string, unknown>;
    const arr = obj.sprints;
    if (!Array.isArray(arr)) return [];
    return arr
        .map((entry): {
            id: string;
            name: string;
            startDate: string;
            endDate: string;
            color?: string;
        } | null => {
            if (!entry || typeof entry !== 'object') return null;
            const e = entry as Record<string, unknown>;
            if (typeof e.id !== 'string') return null;
            if (typeof e.name !== 'string') return null;
            if (typeof e.startDate !== 'string') return null;
            if (typeof e.endDate !== 'string') return null;
            const color = typeof e.color === 'string' ? e.color : undefined;
            return {
                id: e.id,
                name: e.name,
                startDate: e.startDate,
                endDate: e.endDate,
                color,
            };
        })
        .filter((s): s is {
            id: string;
            name: string;
            startDate: string;
            endDate: string;
            color?: string;
        } => s !== null);
}
